import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toPng } from "html-to-image";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Download, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ShareCardStory, SITE } from "@/components/ShareCardStory";
import { cardFontCSS } from "@/lib/cardFonts";

export function ShareArchetype({ arch, dimension, level, scores, variant = "outline" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);
  const [logo, setLogo] = useState(null);
  const scale = Math.min(1, (window.innerHeight - 150) / 640, (window.innerWidth - 40) / 360);

  useEffect(() => {
    if (!open) return;
    cardFontCSS();
    if (logo) return;
    fetch("/logo-horizontal.png")
      .then((r) => r.blob())
      .then((b) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); }))
      .then(setLogo)
      .catch(() => {});
  }, [open, logo]);

  const render = async () => {
    if (document.fonts?.ready) await document.fonts.ready;
    const fontEmbedCSS = await cardFontCSS();
    const opts = { pixelRatio: 3, backgroundColor: "#09090B", fontEmbedCSS, skipFonts: !fontEmbedCSS };
    await toPng(cardRef.current, opts);
    return toPng(cardRef.current, opts);
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = await render();
      const a = document.createElement("a");
      a.href = url;
      a.download = `coach2go-${dimension}-${level}.png`;
      a.click();
      toast.success("Immagine scaricata");
    } catch {
      toast.error("Impossibile generare l'immagine");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const url = await render();
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], `coach2go-${dimension}-${level}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Il mio profilo Coach2Go", text: `Il mio profilo Coach2Go è "${arch.title.replace(/^Profilo\s+/i, "")}". E tu che profilo sei? Scoprilo in 2 minuti: https://${SITE}` });
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        a.click();
        toast.success("Immagine scaricata (condivisione non supportata qui)");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione non riuscita");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="share-archetype-button"
        className={variant === "gold"
          ? "btn-gold inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm uppercase tracking-wide"
          : "btn-outline-gold inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm"}
      >
        <Share2 size={16} /> Condividi profilo
      </button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-5 backdrop-blur-sm"
              onClick={() => !busy && setOpen(false)}
              data-testid="share-modal"
            >
              <motion.div
                initial={{ scale: 0.92, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.92, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                style={{ width: 360 * scale }}
              >
                <div className="flex justify-end">
                  <button onClick={() => setOpen(false)} data-testid="share-close" className="mb-2 flex h-9 w-9 items-center justify-center rounded-full border border-[#27272A] bg-[#121215] text-[#B4B4BC]">
                    <X size={18} />
                  </button>
                </div>
                <div className="overflow-hidden rounded-2xl border border-[#27272A] shadow-2xl" style={{ width: 360 * scale, height: 640 * scale }}>
                  <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: 360, height: 640 }}>
                    <ShareCardStory innerRef={cardRef} arch={arch} dimension={dimension} level={level} logo={logo} scores={scores} />
                  </div>
                </div>
                <div className="mt-4 flex gap-3">
                  <button onClick={share} disabled={busy} data-testid="share-do-button" className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm uppercase tracking-wide">
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Condividi
                  </button>
                  <button onClick={download} disabled={busy} data-testid="share-download-button" className="btn-outline-gold flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-3 text-sm">
                    <Download size={16} /> Scarica
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
